// ** react imports
import React, { useState, useEffect } from 'react';

// ** react router imports
import { useNavigate } from 'react-router-dom';

// ** api hooks
import { useGetAllNotifications } from '../../../hooks/apis/useNotification';

// ** views
import NotificationTable from '../../../views/notification/NotificationList/NotificationTable';
import NotificationPagination from '../../../views/notification/NotificationList/NotificationPagination';
import NotificationFilterButton from '../../../views/notification/NotificationList/NotificationFilterButton';

// ** components imports
import NormalButton from '../../../components/common/Buttons/NormalButton';

// ** enums imports
import { BUTTON_THEMES } from '../../../enums/common';

const NotificationList = () => {
  // ** state management
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [isRead, setIsRead] = useState(undefined);
  const [priority, setPriority] = useState('');
  const [type, setType] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // ** hooks
  const navigate = useNavigate();

  // ** api hooks
  const {
    data,
    isGetAllNotificationsLoading,
    isGetAllNotificationsFetching,
    getAllNotifications,
    refetchGetAllNotifications,
  } = useGetAllNotifications();

  // ** handle search
  const handleSearchChange = searchTerm => {
    setSearch(searchTerm);
    setPage(1); // Reset to first page on search
  };

  // ** handle category filter
  const handleCategoryChange = categoryValue => {
    setCategory(categoryValue);
    setPage(1); // Reset to first page on filter change
  };

  // ** handle status filter
  const handleStatusChange = (field, value) => {
    if (field === 'isRead') {
      setIsRead(value);
    } else if (field === 'priority') {
      setPriority(value);
    } else if (field === 'type') {
      setType(value);
    }
    setPage(1); // Reset to first page on filter change
  };

  // ** handle filter reset
  const handleResetFilters = () => {
    setCategory('');
    setIsRead(undefined);
    setPriority('');
    setType('');
    setSearch('');
    setPage(1);
  };

  // ** handle sort
  const handleSortChange = field => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // ** fetch data when parameters change
  useEffect(() => {
    getAllNotifications({
      page,
      limit,
      category,
      isRead,
      priority,
      type,
      search,
      sortField,
      sortOrder,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    limit,
    category,
    isRead,
    priority,
    type,
    search,
    sortField,
    sortOrder,
  ]);

  // ** Extract data array from the API response structure
  const totalItems = data?.total || 0;
  const totalPages = Math.ceil(totalItems / limit);

  // ** Check if there are active filters
  const hasActiveFilters =
    category !== '' ||
    isRead !== undefined ||
    priority !== '' ||
    type !== '' ||
    search.trim() !== '';

  return (
    <div className="flex ">
      <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-[96rem] mx-auto">
          <div className="sm:flex sm:justify-between sm:items-center mb-6">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                Notifications
              </h1>
            </div>

            <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
              <NormalButton
                theme={BUTTON_THEMES.PRIMARY}
                onClick={() => navigate('/notifications/add')}
              >
                New Notification
              </NormalButton>

              <NotificationFilterButton
                align="right"
                onCategoryChange={handleCategoryChange}
                onStatusChange={handleStatusChange}
                onSearchChange={handleSearchChange}
                onPriorityChange={value =>
                  handleStatusChange('priority', value)
                }
                onTypeChange={value => handleStatusChange('type', value)}
                onReset={handleResetFilters}
                currentFilters={{
                  category,
                  isRead,
                  priority,
                  type,
                  search,
                }}
              />
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mb-4 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-700 transition-all duration-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start sm:items-center gap-2 text-sm text-violet-700 dark:text-violet-300">
                  <svg
                    className="w-4 h-4 mt-0.5 sm:mt-0 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
                    />
                  </svg>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="font-medium whitespace-nowrap">
                      Active Filters:
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {category && (
                        <span className="px-2 py-1 bg-violet-10 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Category: {category}
                        </span>
                      )}
                      {isRead !== undefined && (
                        <span className="px-2 py-1 bg-violet-10 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Status: {isRead ? 'Read' : 'Unread'}
                        </span>
                      )}
                      {priority && (
                        <span className="px-2 py-1 bg-violet-10 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Priority: {priority}
                        </span>
                      )}
                      {type && (
                        <span className="px-2 py-1 bg-violet-10 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Type: {type}
                        </span>
                      )}
                      {search && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Search: {'"'}
                          {search.length > 20
                            ? search.substring(0, 20) + '...'
                            : search}
                          {'"'}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleResetFilters}
                    className="text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 text-sm font-medium whitespace-nowrap transition-colors self-start sm:self-center"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          )}

          <NotificationTable
            data={data}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSortChange}
            isLoading={
              isGetAllNotificationsLoading || isGetAllNotificationsFetching
            }
            refetch={refetchGetAllNotifications}
          />

          <div className="mt-6">
            <NotificationPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={setLimit}
              currentLimit={limit}
              totalItems={totalItems}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationList;
